import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getTrainers } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import TrainerCard from '../components/TrainerCard';
import TrainerMap from '../components/TrainerMap';
import SEO from '../components/SEO';
import { Search, List as ListIcon, Map as MapIcon, SlidersHorizontal, MapPin } from 'lucide-react';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const CATEGORY_PILLS = [
  { key: '', label: 'All' },
  { key: 'jiujitsu', label: 'BJJ' },
  { key: 'personal_training', label: 'Personal Training' },
  { key: 'self_defense', label: 'Self-Defense' },
];

// Haversine distance in miles
function haversineMiles(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => v == null || Number.isNaN(v))) return null;
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 3958.8; // miles
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function isAvailableToday(profile) {
  const sched = profile?.weekly_schedule || {};
  const today = sched[DAY_KEYS[new Date().getDay()]];
  return !!(today && today.enabled);
}

const TrainerSearch = () => {
  const [searchParams] = useSearchParams();
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'map'
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTrainerId, setSelectedTrainerId] = useState(null);
  const [userCoords, setUserCoords] = useState(null); // {lat, lng}
  const [geoStatus, setGeoStatus] = useState('idle'); // idle | requesting | ok | denied
  const cardRefs = useRef({});
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    city: '',
    equipmentOnly: false,
    experienceLevel: '',
    priceMax: 300,
    rating: 0,
    onlineOnly: false,
    availableToday: false,
    radiusMiles: 0, // 0 = any
  });

  const loadTrainers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.city) params.city = filters.city;
      if (filters.onlineOnly) params.online_only = true;

      const response = await getTrainers(params);
      let list = response.data || [];

      // Client-side filters
      if (filters.equipmentOnly) {
        list = list.filter((t) => {
          const e = t.trainer_profile?.equipment;
          return e === 'full' || e === 'some';
        });
      }
      if (filters.experienceLevel) {
        list = list.filter((t) =>
          (t.trainer_profile?.experience_levels || []).includes(filters.experienceLevel)
        );
      }
      if (filters.priceMax && filters.priceMax < 300) {
        list = list.filter((t) => {
          const tp = t.trainer_profile || {};
          const rates = [tp.price_personal_training, tp.price_self_defense, tp.price_jiu_jitsu, tp.hourly_rate]
            .filter((x) => x != null && x > 0);
          const min = rates.length ? Math.min(...rates) : 0;
          return min === 0 || min <= filters.priceMax;
        });
      }
      if (filters.rating > 0) {
        list = list.filter((t) => (t.avg_rating || 0) >= filters.rating);
      }
      if (filters.availableToday) {
        list = list.filter((t) => isAvailableToday(t.trainer_profile));
      }
      if (filters.radiusMiles > 0 && userCoords) {
        list = list
          .map((t) => {
            const lat = t.trainer_profile?.live_lat ?? t.trainer_profile?.lat;
            const lng = t.trainer_profile?.live_lng ?? t.trainer_profile?.lng;
            const dist = haversineMiles(userCoords.lat, userCoords.lng, lat, lng);
            return { ...t, _distance_miles: dist };
          })
          .filter((t) => t._distance_miles != null && t._distance_miles <= filters.radiusMiles)
          .sort((a, b) => (a._distance_miles || 0) - (b._distance_miles || 0));
      }
      setTrainers(list);
    } catch (error) {
      console.error('Failed to load trainers', error);
    } finally {
      setLoading(false);
    }
  }, [filters, userCoords]);

  const matchCount = useMemo(() => trainers.length, [trainers]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoStatus('denied');
      return;
    }
    setGeoStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoStatus('ok');
      },
      () => setGeoStatus('denied'),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  }, []);

  const clearFilters = () => setFilters({
    category: '', city: '', equipmentOnly: false, experienceLevel: '',
    priceMax: 300, rating: 0, onlineOnly: false, availableToday: false, radiusMiles: 0,
  });

  // Count of *drawer* filters (secondary) for the Filters button badge
  const drawerCount = useMemo(() => {
    let n = 0;
    if (filters.equipmentOnly) n++;
    if (filters.experienceLevel) n++;
    if (filters.priceMax < 300) n++;
    if (filters.rating > 0) n++;
    if (filters.onlineOnly) n++;
    if (filters.availableToday) n++;
    if (filters.radiusMiles > 0) n++;
    return n;
  }, [filters]);

  const activeCount = useMemo(() => {
    let n = drawerCount;
    if (filters.category) n++;
    if (filters.city) n++;
    return n;
  }, [drawerCount, filters.category, filters.city]);

  useEffect(() => {
    loadTrainers();
  }, [loadTrainers]);

  // Scroll to + highlight the coach card when a map pin is clicked
  useEffect(() => {
    if (selectedTrainerId && cardRefs.current[selectedTrainerId]) {
      cardRefs.current[selectedTrainerId].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [selectedTrainerId]);

  const mappableCount = trainers.filter(
    (t) => typeof t.trainer_profile?.lat === 'number' && typeof t.trainer_profile?.lng === 'number'
  ).length;

  const chip = (active, extra = '') =>
    `text-xs px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
      active
        ? 'bg-[#C0A062]/15 border-[#C0A062]/50 text-[#C0A062]'
        : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'
    } ${extra}`;

  const greenChip = (active) =>
    `text-xs px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
      active
        ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
        : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'
    }`;

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8" data-testid="trainer-search">
      <SEO
        title="Find a Personal Trainer or BJJ Coach | DojoDash"
        description="Search professional coaches near you. Filter by specialty, price, rating, and experience level. Book in seconds, the coach comes to you."
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Find Your Trainer</h1>
            <p className="text-zinc-400">Browse professional coaches in your area</p>
          </div>

          {/* View Toggle */}
          <div className="inline-flex rounded-lg border border-zinc-800 bg-[#18181B] p-1 self-start sm:self-end" role="tablist" data-testid="view-toggle">
            <button
              type="button"
              onClick={() => setView('list')}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${
                view === 'list' ? 'bg-[#C0A062] text-black font-semibold' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="view-list-btn"
              aria-pressed={view === 'list'}
            >
              <ListIcon className="w-4 h-4" />
              List
            </button>
            <button
              type="button"
              onClick={() => setView('map')}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${
                view === 'map' ? 'bg-[#C0A062] text-black font-semibold' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="view-map-btn"
              aria-pressed={view === 'map'}
            >
              <MapIcon className="w-4 h-4" />
              Map
              {view !== 'map' && mappableCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold bg-zinc-700 text-zinc-200 rounded-full min-w-[18px] h-[18px] px-1">
                  {mappableCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Single-row sticky filter bar */}
        <div className="sticky top-0 z-40 bg-[#09090B]/95 backdrop-blur-sm py-3 -mt-2 mb-1 border-b border-zinc-800/70" data-testid="filter-bar">
          <div className="flex items-center gap-3">
            {/* City search */}
            <div className="relative w-[45%] sm:w-[220px] shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search by city..."
                value={filters.city}
                onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                className="pl-10 bg-zinc-800/50 border-zinc-700 text-white h-9"
                data-testid="search-city"
              />
            </div>

            {/* Category pills */}
            <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="category-pills">
              {CATEGORY_PILLS.map((c) => (
                <button
                  key={c.key || 'all'}
                  type="button"
                  onClick={() => setFilters({ ...filters, category: c.key })}
                  className={chip(filters.category === c.key)}
                  data-testid={`category-pill-${c.key || 'all'}`}
                  aria-pressed={filters.category === c.key}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {/* Filters drawer trigger */}
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-9 shrink-0"
              onClick={() => setDrawerOpen(true)}
              data-testid="filters-drawer-open"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {drawerCount > 0 && <span className="ml-1.5 text-[#C0A062] font-semibold" data-testid="filters-badge">· {drawerCount}</span>}
            </Button>
          </div>
        </div>

        {/* Match count below the bar */}
        <p className="text-xs text-zinc-500 mb-6 mt-2" data-testid="filter-match-count">
          {matchCount} coach{matchCount === 1 ? '' : 'es'} match your filters.
          {activeCount > 0 && (
            <button type="button" onClick={clearFilters} className="ml-2 text-zinc-400 hover:text-white underline" data-testid="clear-filters">
              Clear all
            </button>
          )}
        </p>

        {/* Secondary filters drawer */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="right" className="bg-[#18181B] border-zinc-800 text-white w-full sm:max-w-md overflow-y-auto" data-testid="filters-drawer">
            <SheetHeader>
              <SheetTitle className="text-white">Filters</SheetTitle>
              <SheetDescription className="text-zinc-500">Refine your coach search.</SheetDescription>
            </SheetHeader>

            <div className="space-y-6 mt-6">
              {/* Price */}
              <div data-testid="drawer-price-section">
                <p className="text-sm font-semibold text-zinc-300 mb-3">Max price</p>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="40"
                    max="300"
                    step="5"
                    value={filters.priceMax}
                    onChange={(e) => setFilters({ ...filters, priceMax: Number(e.target.value) })}
                    className="accent-[#C0A062] flex-1"
                    data-testid="filter-price-slider"
                    aria-label="Max price"
                  />
                  <span className="text-[#C0A062] font-semibold w-12 text-right">${filters.priceMax}</span>
                </div>
              </div>

              {/* Rating */}
              <div data-testid="drawer-rating-section">
                <p className="text-sm font-semibold text-zinc-300 mb-3">Rating</p>
                <div className="flex items-center gap-2">
                  {[{ v: 0, label: 'Any' }, { v: 4, label: '4+' }, { v: 4.5, label: '4.5+' }].map((r) => (
                    <button
                      key={r.v}
                      type="button"
                      onClick={() => setFilters({ ...filters, rating: r.v })}
                      className={chip(filters.rating === r.v)}
                      data-testid={`filter-rating-${r.v}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div data-testid="drawer-availability-section">
                <p className="text-sm font-semibold text-zinc-300 mb-3">Availability</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setFilters({ ...filters, equipmentOnly: !filters.equipmentOnly })}
                    className={greenChip(filters.equipmentOnly)}
                    data-testid="filter-equipment-toggle"
                  >
                    Brings equipment
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilters({ ...filters, onlineOnly: !filters.onlineOnly })}
                    className={greenChip(filters.onlineOnly)}
                    data-testid="filter-online-toggle"
                  >
                    Online now
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilters({ ...filters, availableToday: !filters.availableToday })}
                    className={greenChip(filters.availableToday)}
                    data-testid="filter-available-today"
                  >
                    Available today
                  </button>
                </div>
              </div>

              {/* Skill level */}
              <div data-testid="drawer-skill-section">
                <p className="text-sm font-semibold text-zinc-300 mb-3">Skill level</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'beginner', label: 'Beginner-friendly' },
                    { id: 'intermediate', label: 'Intermediate' },
                    { id: 'advanced', label: 'Advanced' },
                  ].map((lvl) => (
                    <button
                      type="button"
                      key={lvl.id}
                      onClick={() => setFilters({ ...filters, experienceLevel: filters.experienceLevel === lvl.id ? '' : lvl.id })}
                      className={chip(filters.experienceLevel === lvl.id)}
                      data-testid={`filter-exp-${lvl.id}`}
                    >
                      {lvl.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Distance */}
              <div data-testid="drawer-distance-section">
                <p className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-[#C0A062]" /> Distance
                </p>
                {!userCoords ? (
                  <button
                    type="button"
                    onClick={requestLocation}
                    disabled={geoStatus === 'requesting'}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#C0A062]/40 text-[#C0A062] hover:bg-[#C0A062]/10 disabled:opacity-60"
                    data-testid="enable-location-btn"
                  >
                    {geoStatus === 'requesting' ? 'Requesting…' : geoStatus === 'denied' ? 'Location blocked' : 'Use my location'}
                  </button>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    {[5, 10, 25, 50].map((mi) => (
                      <button
                        key={mi}
                        type="button"
                        onClick={() => setFilters({ ...filters, radiusMiles: filters.radiusMiles === mi ? 0 : mi })}
                        className={chip(filters.radiusMiles === mi)}
                        data-testid={`filter-radius-${mi}`}
                      >
                        {mi} mi
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => { setUserCoords(null); setGeoStatus('idle'); setFilters({ ...filters, radiusMiles: 0 }); }}
                      className="text-[11px] text-zinc-500 hover:text-zinc-300 underline"
                      data-testid="reset-location-btn"
                    >
                      reset
                    </button>
                  </div>
                )}
                {geoStatus === 'denied' && (
                  <p className="text-[11px] text-amber-400/80 mt-2">Enable location in your browser to use distance filtering.</p>
                )}
              </div>
            </div>

            {/* Drawer footer */}
            <div className="mt-8 pt-4 border-t border-zinc-800 flex items-center gap-3">
              <Button
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 flex-1"
                onClick={clearFilters}
                data-testid="drawer-clear-filters"
              >
                Clear all
              </Button>
              <Button
                className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold flex-1"
                onClick={() => setDrawerOpen(false)}
                data-testid="drawer-show-results"
              >
                Show {matchCount} result{matchCount === 1 ? '' : 's'}
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : trainers.length === 0 ? (
          activeCount > 0 ? (
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-10 text-center" data-testid="filters-empty-state">
              <p className="text-white font-semibold mb-2">No coaches match your filters</p>
              <p className="text-zinc-400 text-sm mb-4">Try loosening your filters, your search is more specific than our current roster.</p>
              <Button onClick={clearFilters} className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold" data-testid="filters-empty-clear">
                Clear all filters
              </Button>
            </div>
          ) : (
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-10 text-center" data-testid="empty-trainers-state">
              <p className="text-white font-semibold mb-2">No coaches available in your area yet</p>
              <p className="text-zinc-400 text-sm">
                {filters.city?.trim() ? `We don't have coaches active in ${filters.city.trim()} right now. ` : ''}
                Check back soon — new coaches join every week.
              </p>
            </div>
          )
        ) : (
          <>
            <p className="text-sm text-zinc-400 mb-6">
              {trainers.length} trainer{trainers.length !== 1 ? 's' : ''} found
              {view === 'map' && mappableCount < trainers.length && (
                <span className="text-zinc-500"> · {trainers.length - mappableCount} hidden on map (no location set)</span>
              )}
            </p>

            {view === 'list' ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="trainer-grid">
                {trainers.map((trainer) => (
                  <TrainerCard key={trainer.id} trainer={trainer} />
                ))}
              </div>
            ) : (
              /* Split view: list left (40%) / map right (60%). Mobile: map top, horizontal strip below */
              <div className="flex flex-col lg:flex-row gap-4" data-testid="map-split-view">
                <div
                  className="order-2 lg:order-1 lg:w-2/5 flex gap-4 overflow-x-auto pb-2 lg:pb-0 lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto lg:max-h-[600px] lg:pr-2"
                  data-testid="map-side-list"
                >
                  {trainers.map((trainer) => (
                    <div
                      key={trainer.id}
                      ref={(el) => { cardRefs.current[trainer.id] = el; }}
                      className={`min-w-[300px] sm:min-w-[340px] lg:min-w-0 rounded-lg transition-shadow ${
                        selectedTrainerId === trainer.id ? 'ring-2 ring-[#C0A062]' : ''
                      }`}
                      data-testid={`map-list-card-${trainer.id}`}
                    >
                      <TrainerCard trainer={trainer} />
                    </div>
                  ))}
                </div>
                <div className="order-1 lg:order-2 lg:w-3/5">
                  <TrainerMap
                    trainers={trainers}
                    onPinClick={setSelectedTrainerId}
                    heightClass="h-[380px] lg:h-[600px]"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TrainerSearch;
